/**
 * App bootstrap: providers, router, i18n init.
 *
 * Provider stack (outermost → innermost):
 *   QueryClientProvider  — TanStack Query (T031)
 *   BrowserRouter        — React Router 7 (T028)
 *   <App>                — routes
 *
 * i18next is initialised by importing './i18n' for its side effects;
 * react-i18next's <Trans>/useTranslation pick it up via the default i18n
 * instance, so no provider is needed.
 *
 * The Zustand store (T030) needs no provider — components import the
 * hook directly.
 */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import './i18n/index.js'
import './styles/global.css'
import { App } from './App.js'
import { installDebug, DebugRoot } from './debug/index.js'

// Debug/diagnostics mode (009). No-op unless ?debug=1 — must run before any
// RTCPeerConnection is created so the stats tap can collect them.
installDebug()

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element #root not found')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Realtime app — we rarely want stale data hanging around.
      staleTime: 0,
      // Default retries are noisy in dev when the server is down.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <DebugRoot />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
